import { Theme, alpha, Components } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { typographyClasses } from '@mui/material/Typography';
import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { chipClasses } from '@mui/material/Chip';
import { iconButtonClasses } from '@mui/material/IconButton';

/* eslint-disable import/prefer-default-export */
export const dataDisplayCustomizations: Components<Theme> = {
  MuiList: {
    styleOverrides: {
      root: {
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${svgIconClasses.root}`]: {
          width: '1rem',
          height: '1rem',
          color: (theme.vars || theme).palette.text.secondary,
        },
        [`& .${typographyClasses.root}`]: {
          fontWeight: 500,
        },
        [`& .${buttonBaseClasses.root}`]: {
          display: 'flex',
          gap: 8,
          padding: '2px 8px',
          borderRadius: (theme.vars || theme).shape.borderRadius,
          opacity: 0.7,
          '&.Mui-selected': {
            opacity: 1,
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
            [`& .${svgIconClasses.root}`]: {
              color: (theme.vars || theme).palette.text.primary,
            },
            '&:focus-visible': {
              backgroundColor: alpha(theme.palette.action.selected, 0.3),
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.selected, 0.5),
            },
          },
          '&:focus-visible': {
            backgroundColor: 'transparent',
          },
        },
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.body2.lineHeight,
      }),
      secondary: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: 'transparent',
        padding: '4px 8px',
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 0,
      },
    },
  },
  MuiChip: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: ({ theme }) => ({
        border: '1px solid transparent',
        borderRadius: '999px',
        padding: '2px 0',
        [`& .${chipClasses.label}`]: { fontWeight: 500, fontSize: 'var(--fs-xs)', px: '8px' },
        variants: [
          {
            props: { color: 'default' },
            style: {
              backgroundColor: 'var(--surface-2)',
              borderColor: 'var(--border)',
              [`& .${chipClasses.label}`]: { color: 'var(--muted)' },
              [`& .${chipClasses.icon}`]: { color: 'var(--muted)' },
            },
          },
          {
            props: { color: 'success' },
            style: {
              backgroundColor: 'var(--success-bg)',
              borderColor: 'transparent',
              [`& .${chipClasses.label}`]: { color: 'var(--success)' },
              [`& .${chipClasses.icon}`]: { color: 'var(--success)' },
            },
          },
          {
            props: { color: 'error' },
            style: {
              backgroundColor: 'var(--danger-bg)',
              borderColor: 'transparent',
              [`& .${chipClasses.label}`]: { color: 'var(--danger)' },
              [`& .${chipClasses.icon}`]: { color: 'var(--danger)' },
            },
          },
          {
            props: { color: 'warning' },
            style: {
              backgroundColor: 'var(--warning-bg)',
              borderColor: 'transparent',
              [`& .${chipClasses.label}`]: { color: 'var(--warning)' },
              [`& .${chipClasses.icon}`]: { color: 'var(--warning)' },
            },
          },
          {
            props: { color: 'info' },
            style: {
              backgroundColor: 'var(--info-bg)',
              borderColor: 'transparent',
              [`& .${chipClasses.label}`]: { color: 'var(--info)' },
              [`& .${chipClasses.icon}`]: { color: 'var(--info)' },
            },
          },
          {
            props: { color: 'primary' },
            style: {
              backgroundColor: 'var(--accent-soft)',
              borderColor: 'transparent',
              [`& .${chipClasses.label}`]: { color: 'var(--accent)' },
              [`& .${chipClasses.icon}`]: { color: 'var(--accent)' },
            },
          },
          {
            props: { size: 'small' },
            style: {
              maxHeight: 22,
              [`& .${chipClasses.label}`]: { fontSize: 'var(--fs-xs)' },
              [`& .${svgIconClasses.root}`]: { fontSize: 11 },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              [`& .${chipClasses.label}`]: { fontSize: 'var(--fs-sm)' },
            },
          },
        ],
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          backgroundColor: 'var(--surface-2)',
          color: 'var(--muted)',
          fontSize: 'var(--fs-xs)',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          padding: '0 16px',
          height: 40,
          borderBottom: '1px solid var(--border)',
        },
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': {
          height: 'var(--row-h)',
          '&:hover': { backgroundColor: 'var(--surface-2)' },
        },
        '& .MuiTableCell-body': {
          fontSize: 'var(--fs-base)',
          padding: '0 16px',
          color: 'var(--ink)',
          borderBottom: '1px solid var(--border)',
          verticalAlign: 'middle',
        },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      actions: {
        display: 'flex',
        gap: 8,
        marginRight: 6,
        [`& .${iconButtonClasses.root}`]: {
          minWidth: 0,
          width: 36,
          height: 36,
        },
      },
    },
  },
  MuiIcon: {
    defaultProps: {
      fontSize: 'small',
    },
    styleOverrides: {
      root: {
        variants: [
          {
            props: {
              fontSize: 'small',
            },
            style: {
              fontSize: '1rem',
            },
          },
        ],
      },
    },
  },
};
